import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeItem, total } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (items.length === 0) return;
    if (!user) { navigate("/login?next=/cart"); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) =>
          i.kind === "bundle"
            ? { bundle_id: i.bundle_id, quantity: 1 }
            : { product_id: i.product_id, quantity: 1 }
        ),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setLoading(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO title="Cart" description="Your South on Leadership cart." noindex />
      <section className="container-narrow py-16 md:py-24">
        <span className="overline text-brand-rust">Step 1 of 2</span>
        <h1 className="font-display text-5xl md:text-6xl mt-3 text-brand-ink">Your cart</h1>

        {items.length === 0 ? (
          <div className="mt-16 border border-brand-line bg-white p-16 text-center" data-testid="cart-empty">
            <ShoppingBag className="w-8 h-8 text-brand-inkMuted mx-auto" />
            <p className="font-serif text-2xl mt-4 text-brand-ink">Your cart is empty.</p>
            <p className="text-sm text-brand-inkMuted mt-2">Browse the library to find your next read.</p>
            <Link to="/books" className="inline-block mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide" data-testid="cart-shop-books">
              Browse books
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <ul className="lg:col-span-8 divide-y divide-brand-line border-y border-brand-line">
              {items.map((it) => {
                const key = it.kind === "bundle" ? it.bundle_id : it.product_id;
                return (
                  <li key={key} className="py-6 flex items-center gap-5" data-testid={`cart-item-${key}`}>
                    <div className="w-20 h-24 flex-none bg-brand-bg overflow-hidden">
                      <img src={it.cover_image} alt={it.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <span className="overline text-brand-inkMuted">{it.kind === "bundle" ? `bundle · ${it.item_count} items` : it.type}</span>
                      <p className="font-serif text-xl text-brand-ink leading-snug">{it.title}</p>
                      <p className="text-sm text-brand-inkMuted">{it.kind === "bundle" ? it.subtitle : (it.author || it.instructor)}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-brand-ink font-medium">${Number(it.price).toFixed(2)}</div>
                      {it.kind === "bundle" && it.savings > 0 && (
                        <div className="overline text-brand-rust mt-1">save ${Number(it.savings).toFixed(0)}</div>
                      )}
                    </div>
                    <button
                      data-testid={`cart-remove-${key}`}
                      onClick={() => removeItem(key)}
                      className="text-brand-inkMuted hover:text-brand-rust transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>

            <aside className="lg:col-span-4">
              <div className="bg-white border border-brand-line p-8 lg:sticky lg:top-28">
                <span className="overline text-brand-rust">Order summary</span>
                <div className="mt-6 flex items-center justify-between text-brand-ink">
                  <span>Subtotal</span><span data-testid="cart-subtotal">${total.toFixed(2)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-brand-inkMuted text-sm">
                  <span>Taxes</span><span>Calculated at checkout</span>
                </div>
                <div className="mt-6 border-t border-brand-line pt-6 flex items-end justify-between">
                  <span className="overline text-brand-inkMuted">Total</span>
                  <span className="font-serif text-3xl text-brand-ink" data-testid="cart-total">${total.toFixed(2)}</span>
                </div>
                <button
                  data-testid="checkout-btn"
                  onClick={checkout}
                  disabled={loading}
                  className="mt-8 w-full bg-brand-rust text-white hover:bg-brand-rustDark py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
                >
                  {loading ? "Redirecting…" : user ? "Proceed to checkout" : "Sign in to checkout"}
                </button>
                <p className="text-xs text-brand-inkMuted mt-3 text-center">Secure payment via Stripe</p>
              </div>
            </aside>
          </div>
        )}
      </section>
    </Layout>
  );
}
